import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { appConfig } from "../../config"
import { EU_UMBRELLA_ORIGIN_SRC, MAX_ORIGIN_SRC_LENGTH } from "../../utils/constants/DOMAINS"
import { EU_EEA_COUNTRY_CODES, isEuOriginSrc } from "../../utils/euCountries"
import { BREAKPOINTS, KNOWN_ORGS, Language, PortalAccountDomain, ScreenSize, TradeAccountType, __STORAGE } from '../../utils/enums'
import { AccountStat, AccountTradingTypeModel, AccountTradingTypeUnion, AppInfo, ApplicationApproval, ApplicationStatus, ApplicationType, AppropriatenessLevel, BankAccount, CreateTradingAccountParams, CurrencyPairRateResponse, DemoActivationResult, DocType, FA2, finishedStatuses, GetIPResponse, KYCProvider, LatestTermsAndConditions, LoginResponse, MigrationInfo, NewCreatedAccount, NewTradeAccountInfo, Organization, OrganizationFeature, OrganizationSettings, PreferredLanguage, Question, SubscriptionStatus, SupportRequestType, TradingAccount, TradingAccountType, TradingPlatform, TradingPlatformName, UserApplication, UserProfile, UserReferrerStatus } from '../../utils/types'
import Utils from "../../utils/utils"
import { AppError, AppState, LoyaltyStatus } from '../appState'
import { Country, IBInfo, Notification, NotificationMap, Transaction } from './../../utils/types'
import * as ApiReducers from './apiReducers'

const getScreenType = (width: number): ScreenSize => {
    switch (true) {
        case width < BREAKPOINTS.MOBILE:
            return ScreenSize.Mobile
        case width > BREAKPOINTS.DESKTOP:
            return ScreenSize.Desktop
        default:
            return ScreenSize.Tablet
    }
}

const initialState: AppState = {
    autoLogout: false,
    debugMode: false,
    busy: false,
    loggedIn: false,
    initError: null,
    canCreateAccounts: true,
    language: localStorage.language ? localStorage.language : 'en',
    screen: getScreenType(window.innerWidth),
    rtl: localStorage.language ? localStorage.language === 'ar' : false,
    portalAccountDomain: undefined,
    onboardingDomain: null,
    ibInfo: null,
    totalBalanceInUSD: 0,
    appStatus: null,
    oldAppStatus: null,
    appropriatenessLevel: null,
    applicationApproval: null,
    appToken: null,
    error: null,
    userIP: null,
    userLoginResult: "",
    userLoginInfo: null,
    userProfile: null,
    userApps: [],
    uncompletedApplication: {},
    newTradeAccountInfo: null,
    onboardingCountry: undefined,
    demoPlatforms: [],
    livePlatforms: [],
    demoAccounts: [],
    liveAccounts: [],
    accountsLoaded: false,
    bankAccounts: null,
    countries: [],
    phoneCountries: [],
    docTypes: [],
    preferredLanguages: null,
    organizationSettings: null,
    otpCodeSent: false,
    isNewPortalAccount: false,
    pageTitle: null,
    passwordResetToken: null,
    userEmailVerified: false,
    userEmailPreverified: false,
    jumioFailed: false,
    showAppSidebar: false,
    notifications: [],
    notificationMap: [],
    twoFa: {
        enabled: false,
        checked: false,
        valid: false
    },
    creatingLive: false,
    hasCompletedApp: false,
    lastDeposit: null,
    lastWithdraw: null,
    liveAppCompleted: false,
    KYCProvider: null,
    migration: null,
    nextTermsAndConditions: null,
    isDemoExpired: false,
    isSubscriptionTerminated: false,
    isMigrationModalShown: false,
    newCreatedAccount: null,
    tradingTypes: null,
    questions: null,
    subscriptionStatus: 'noRecord',
    liveCongratsShown: false,
    originalExchangeRate: null,
    organizations: [],
    tradingAccountToCreate: "CFD",
    allowThinkInvest: true,
    redirectedFromWTR: false,
    additionalCFD: null,
    saveWtrFundsRedirection: false,
    showFA2Warning: false,
    wtrFundsRoute: '',
    syOrgCountries: [],
    showCryptoBanner: false,
    supportRequestTypes: [],
    blockedDepositAmountEnabled: false,
    userReferrerStatus: null,
    userInsidePortal: false,
    originSrc: null,
    inactivityTimeout: 360,
    allowedPlatforms: undefined,
    loyaltyStatus: null,
    applicationCooldownCheck: false,
    bootCompletionCount: 0,
    demoActivationPending: localStorage.getItem(__STORAGE.demoActivation) === '1',
    demoActivationResults: [],
    demoActivationPortalPassword: undefined,
}


let appState = initialState
if (localStorage[__STORAGE.userLoginInfo]) {
    const info = Utils.loadEncryptedObject<LoginResponse>(__STORAGE.userLoginInfo)
    if (Object.entries(info).length > 0)
        appState = { ...appState, userLoginInfo: info }
}

if (localStorage[__STORAGE.newAccount]) {
    const info = Utils.loadEncryptedObject<NewTradeAccountInfo>(__STORAGE.newAccount)
    appState = { ...appState, newTradeAccountInfo: info }
}

if (localStorage[__STORAGE.liveCongratsShown]) {
    const liveCongratsShown = Utils.getStorageBoolean(__STORAGE.liveCongratsShown)
    appState = { ...appState, liveCongratsShown: liveCongratsShown }
}

const storedOriginSrc = sessionStorage.getItem(__STORAGE.originSrc)
if (storedOriginSrc) {
    appState = { ...appState, originSrc: storedOriginSrc.slice(0, MAX_ORIGIN_SRC_LENGTH) }
} else if (sessionStorage.getItem('__eu__') === '1') {
    // Migrate the legacy boolean euOrigin flag (renamed to originSrc): a session
    // that landed from an EU page before this deploy would otherwise lose the
    // EU/EEA country filter mid-flow. Map it to the umbrella token and drop the old key.
    appState = { ...appState, originSrc: EU_UMBRELLA_ORIGIN_SRC }
    sessionStorage.setItem(__STORAGE.originSrc, EU_UMBRELLA_ORIGIN_SRC)
    sessionStorage.removeItem('__eu__')
}

const slice = createSlice({
    name: "app",
    initialState: appState,
    reducers: {
        // THPRT-6209: boot saga puts this at every landing point; App reacts to the count.
        bootCompleted: (state: AppState) => {
            state.bootCompletionCount += 1
        },
        debugMode: (state: AppState, action: PayloadAction<boolean>) => {
            state.debugMode = action.payload
        },
        changeScreen: (state: AppState, action: PayloadAction<ScreenSize>) => {
            state.screen = action.payload
        },
        changeLanguage: (state: AppState, action: PayloadAction<string>) => {
            state.language = (action.payload === 'zh' ? 'zh-Hans' : action.payload) as Language
            state.rtl = action.payload === 'ar'
        },
        setLoggedIn: (state: AppState, action: PayloadAction<boolean>) => {
            state.loggedIn = action.payload
        },
        setAppBusy: (state: AppState, action: PayloadAction<boolean>) => {
            state.busy = action.payload
        },
        setTempData: (state: AppState, action: PayloadAction<any>) => {
            state.tempData = action.payload
        },
        setPageTitle: (state: AppState, action: PayloadAction<string | null>) => {
            state.pageTitle = action.payload
        },
        initError: (state: AppState, action: PayloadAction<Error | null>) => {
            state.initError = action.payload
        },
        appError: (state: AppState, action: PayloadAction<AppError | null>) => {
            const error = action.payload
            if ((error?.type === 'status' && error?.status === 'NOT_AUTHORIZED') ||
                (error?.type === 'custom' && error?.text === 'NOT_AUTHORIZED'))
                return
            state.error = action.payload
        },
        setMigration: (state: AppState, action: PayloadAction<MigrationInfo | null>) => {
            state.migration = action.payload
        },
        setNextTermsAndConditions: (state: AppState, action: PayloadAction<LatestTermsAndConditions | null>) => {
            state.nextTermsAndConditions = action.payload
        },
        setIsDemoExpired: (state: AppState, action: PayloadAction<boolean>) => {
            state.isDemoExpired = action.payload
        },
        setIsSubscriptionTerminated: (state: AppState, action: PayloadAction<boolean>) => {
            state.isSubscriptionTerminated = action.payload
        },
        userLoaded: (state: AppState, action: PayloadAction<UserProfile>) => {
            const profile = action.payload
            state.userProfile = profile
            if (!profile.preferredLanguage)
                profile.preferredLanguage = state.preferredLanguages![0]
            state.userProfile.password = Utils.loadEncryptedString(__STORAGE.u_pass)
            state.onboardingCountry = profile?.country
            state.portalAccountDomain = profile.organization.name
            if (profile.additionalAttributes?.inactivityTimeout)
                state.inactivityTimeout = parseInt(profile.additionalAttributes.inactivityTimeout)
            else state.inactivityTimeout = appConfig.LOGOUT_AFTER_MIN
        },
        setUser: (state: AppState, action: PayloadAction<UserProfile>) => {
            if (!action.payload.preferredLanguage)
                action.payload.preferredLanguage = state.preferredLanguages![0]
            state.userProfile = action.payload
        },
        setNewPortalAccount: (state: AppState, action: PayloadAction<boolean>) => {
            state.isNewPortalAccount = action.payload
        },
        setSubscriptionStatus: (state: AppState, action: PayloadAction<SubscriptionStatus>) => {
            state.subscriptionStatus = action.payload
        },
        userAppsLoaded: (state: AppState, action: PayloadAction<UserApplication[]>) => {
            const apps = action.payload
            if (apps && apps.length) {

                const approved = apps.find(x => x.applicationStatus.toUpperCase() === 'APPROVED')
                if (approved) state.hasCompletedApp = true
                state.userApps = apps
            }
        },
        updateUserAppInfo: (state: AppState, action: PayloadAction<Partial<AppInfo>>) => {
            const { payload } = action
            state.uncompletedApplication = { ...state.uncompletedApplication, ...payload }
        },
        setUserAppInfo: (state: AppState, action: PayloadAction<Partial<AppInfo>>) => {
            const { payload } = action
            if (payload) {
                if (payload.token) {
                    state.appToken = payload.token
                    delete payload.token
                }
                //compatibility with old portal
                if (payload.accountType !== 'corporate') {
                    if (payload.selectedPlatform) payload.selectedPlatform = Utils.string2TradingPlatformName(payload.selectedPlatform)
                    if (!payload.accountHolderPhone && payload.accountHolderMobilePhone)
                        payload.accountHolderPhone = payload.accountHolderMobilePhone
                    if (!payload.accountHolderPhoneCode && payload.accountHolderMobilePhoneCode)
                        payload.accountHolderPhoneCode = payload.accountHolderMobilePhoneCode
                }
            }
            state.uncompletedApplication = payload
        },
        setNewTradeAccountInfo: (state: AppState, action: PayloadAction<Partial<NewTradeAccountInfo> | null>) => {
            if (action.payload) {
                const info = { ...action.payload }
                if (!info.email) info.email = Utils.loadEncryptedString(__STORAGE.user)
                state.newTradeAccountInfo = info
                Utils.saveEncryptedObject(__STORAGE.newAccount, info)
            }
            else {
                state.newTradeAccountInfo = null
                localStorage.removeItem(__STORAGE.newAccount)
            }
        },
        docTypesLoaded: (state: AppState, action: PayloadAction<DocType[]>) => {
            state.docTypes = action.payload
        },
        setCreatingLive: (state: AppState, action: PayloadAction<boolean>) => {
            state.creatingLive = action.payload
        },
        setLastDeposit: (state: AppState, action: PayloadAction<Transaction | null>) => {
            state.lastDeposit = action.payload
            if (state.appStatus === 'APPROVED' && action.payload) state.liveAppCompleted = true
        },
        setLastWithdraw: (state: AppState, action: PayloadAction<Transaction | null>) => {
            state.lastWithdraw = action.payload
        },
        setLiveAppCompleted: (state: AppState, action: PayloadAction<boolean>) => {
            state.liveAppCompleted = action.payload
        },
        setAdditionalCFD: (state: AppState, action: PayloadAction<Partial<CreateTradingAccountParams> | null>) => {
            state.additionalCFD = action.payload
        },
        setOtpCodeSent: (state: AppState, action: PayloadAction<boolean>) => {
            state.otpCodeSent = action.payload
        },
        setKYCProvider: (state: AppState, action: PayloadAction<KYCProvider>) => {
            state.KYCProvider = action.payload
        },
        notificationsLoaded: (state: AppState, action: PayloadAction<Notification[]>) => {
            const active = action.payload.filter(n => n.active)
            try {
                const { notificationMap: map } = state
                if (state.notificationMap.length)
                    active.forEach(n => {
                        const m = map.find(m => m.id === n.notificationId)
                        n.message = m ? m.notification : 'Unknown notification'
                    })
                state.notifications = active
            } catch (e: any) { console.log(e) }
        },
        notificationMapLoaded: (state: AppState, action: PayloadAction<NotificationMap[]>) => {
            state.notificationMap = action.payload
        },
        dismissNotification: (state: AppState, action: PayloadAction<number>) => {
            state.notifications.splice(action.payload, 1)
        },
        setNotificationsRead: (state: AppState) => {
            state.notifications.forEach(n => n.read = true)
        },
        userEmailVerified: (state: AppState, action: PayloadAction<boolean>) => {
            state.userEmailVerified = action.payload
        },
        userEmailPreverified: (state: AppState, action: PayloadAction<boolean>) => {
            state.userEmailPreverified = action.payload
        },
        jumioFailed: (state: AppState, action: PayloadAction<boolean>) => {
            state.jumioFailed = action.payload
        },
        setShowAppSidebar: (state: AppState, action: PayloadAction<boolean>) => {
            state.showAppSidebar = action.payload
        },
        setRedirectedFromWTR: (state: AppState, action: PayloadAction<boolean>) => {
            state.redirectedFromWTR = action.payload
        },
        setOriginSrc: (state: AppState, action: PayloadAction<string | null>) => {
            // Normalise to lower-case here so every consumer (EU_ORIGIN_SRC_TOKENS
            // membership, the initCountries preselection) can rely on it, rather
            // than depending on each callsite to lower-case before dispatching.
            const originSrc = action.payload?.toLowerCase() ?? null
            if (state.originSrc === originSrc) return
            state.originSrc = originSrc
            if (originSrc) sessionStorage.setItem(__STORAGE.originSrc, originSrc)
            else sessionStorage.removeItem(__STORAGE.originSrc)
        },
        setSaveWtrFundsRedirection: (state: AppState, action: PayloadAction<boolean>) => {
            state.saveWtrFundsRedirection = action.payload
        },
        setWtrFundsRoute: (state: AppState, action: PayloadAction<string>) => {
            state.wtrFundsRoute = action.payload
        },
        setTwoFa: (state: AppState, action: PayloadAction<FA2>) => {
            state.twoFa = action.payload
            Utils.saveEncryptedObject(__STORAGE.fa2, state.twoFa)
        },
        initCountries: (state: AppState, action: PayloadAction<{
            ip?: GetIPResponse,
            countries: Country[]
        }>) => {
            const { ip, countries } = action.payload
            state.userIP = ip?.country_code ? ip : null
            const filtered = countries.filter(c => c.used && KNOWN_ORGS.includes(c.organization.name))
            state.countries = filtered
            // Resolve the country to pre-select. A marketing origin path segment
            // names a country and takes priority over IP geolocation: WD-474 segments
            // are ISO alpha-2 codes, with 'uk' the lone non-ISO exception (→ 'gb').
            // Region/language segments (eu, latam, zh-hans) match no country and fall
            // through to the IP country, as do no-origin visitors.
            const originCode2 = state.originSrc === 'uk' ? 'gb' : state.originSrc
            const country =
                (originCode2 ? filtered.find(c => c.code2.toLowerCase() === originCode2) : undefined)
                ?? filtered.find(c => c.code2.toLowerCase() === ip?.country_code?.toLowerCase())

            // …unless an explicit EU origin (?src=eu, /de…) forbids a non-EU country —
            // then leave it unselected. (A no-origin EU IP needs no guard: the matched
            // country is itself EU; a no-origin non-EU IP is unrestricted.)
            if (country && !state.onboardingCountry && !state.portalAccountDomain
                && !(isEuOriginSrc(state.originSrc) && !EU_EEA_COUNTRY_CODES.has(country.code3))) {
                state.onboardingCountry = country
                state.portalAccountDomain = country.organization.name
            }

            const orgs: Organization[] = []
            countries.forEach(c => {
                const exists = orgs.find(o => o.name === c.organization.name)
                if (!exists) orgs.push(c.organization)
            })
            state.phoneCountries = countries
            state.organizations = orgs
        },
        organizationSettingsLoaded: (state: AppState, action: PayloadAction<
            {
                settings: OrganizationSettings
                countryId?: number
            }>) => {

            state.organizationSettings = action.payload.settings

            const { features, organizationId } = action.payload.settings
            const countryId = action.payload.countryId
                ?? state.userProfile?.country?.id
            if (!countryId) return
            const livePlatforms: Array<OrganizationFeature> = []
            const demoPlatforms: Array<OrganizationFeature> = []

            const matchesType = (f: OrganizationFeature, type: string) =>
                f.types.length === 0 || f.types.includes(type)

            const getCountrySpecificFeatures = (platforms: Array<OrganizationFeature>, type: string) => {
                features.forEach(f => {
                    if (!f.hidden &&
                        f.countries.includes(countryId) &&
                        matchesType(f, type))
                        platforms.push(f)
                })
            }
            const getOrgSpecificFeatures = (platforms: Array<OrganizationFeature>, type: string) => {
                features.forEach(f => {
                    if (!f.hidden &&
                        !f.countries.includes(countryId) &&
                        f.organizations.includes(organizationId) &&
                        matchesType(f, type)) {
                        platforms.push(f)
                    }
                })
            }

            getCountrySpecificFeatures(livePlatforms, "LIVE")
            getOrgSpecificFeatures(livePlatforms, "LIVE")
            getCountrySpecificFeatures(demoPlatforms, "DEMO")
            getOrgSpecificFeatures(demoPlatforms, "DEMO")

            const processPlatforms = (type: TradeAccountType, features: OrganizationFeature[]): TradingPlatform[] => {
                const platformsMap = new Map<TradingPlatformName, {
                    name: TradingPlatformName
                    accounts: Map<TradingAccountType, {
                        type: TradingAccountType
                        currencies: Set<string>
                        leverages: Set<number>
                    }>
                }>()

                const getPlatform = (name: TradingPlatformName) => {
                    return features.filter(f => f.platforms.includes(name))
                }

                const addPlatform = (name: TradingPlatformName, features: OrganizationFeature[]) => {
                    if (state.allowedPlatforms === undefined ||
                        state.allowedPlatforms.find(p => p === name)) {
                        if (features.length > 0) {
                            features.forEach(f => {
                                if (f.accountTypes.length === 0) return
                                const leverages = f.leverages
                                const currencies = f.currencies
                                f.accountTypes.forEach(accountType => {
                                    if (!accountType) return
                                    //     //THPRT-5287 remove mini for demo
                                    if (type === TradeAccountType.Demo && accountType === 'Mini') return
                                    let p = platformsMap.get(name)
                                    if (!p) {
                                        platformsMap.set(name, {
                                            name: name,
                                            accounts: new Map()
                                        })
                                        p = platformsMap.get(name)!
                                        p.accounts.set(accountType, {
                                            type: accountType,
                                            currencies: new Set(currencies),
                                            leverages: new Set(leverages)
                                        })
                                    } else {
                                        let a = p.accounts.get(accountType)
                                        if (!a) {
                                            p.accounts.set(accountType, {
                                                type: accountType,
                                                currencies: new Set(currencies),
                                                leverages: new Set(leverages)
                                            })
                                        } else {
                                            leverages.forEach(l => a!.leverages.add(l))
                                            currencies.forEach(c => a!.currencies.add(c))
                                        }
                                    }
                                })
                            })
                        }
                    }
                }

                addPlatform('MT4', getPlatform('MT4'))
                addPlatform('MT5', getPlatform('MT5'))
                addPlatform('ThinkTrader', getPlatform('ThinkTrader'))
                if (type === TradeAccountType.Live) {
                    let p = getPlatform('THINK_COPY_MT5')
                    if (p.length > 0) {
                        addPlatform('THINK_COPY', p)
                    }
                    p = getPlatform('PAMM_MT5')
                    if (p.length > 0) {
                        addPlatform('PAMM_TRADING', p)
                    }
                }
                const platforms: { name: TradingPlatformName; accounts: TradingAccount[] }[] = []
                platformsMap.forEach(p => {
                    const accounts: TradingAccount[] = []
                    p.accounts.forEach(a => {
                        accounts.push({
                            type: a.type,
                            currencies: Array.from(a.currencies).sort(),
                            leverages: Array.from(a.leverages).sort((a, b) => a - b)
                        })
                    })
                    platforms.push({
                        name: p.name,
                        accounts: accounts
                    })
                })
                return platforms

            }
            state.demoPlatforms = processPlatforms(TradeAccountType.Demo, demoPlatforms)
            state.livePlatforms = processPlatforms(TradeAccountType.Live, livePlatforms)
        },
        setUserIP: (state: AppState, action: PayloadAction<GetIPResponse | null>) => {
            const ip = action.payload
            state.userIP = ip?.country_code ? ip : null
        },
        setUserCountry: (state: AppState, action: PayloadAction<Country | undefined>) => {
            const country = action.payload
            if (country) {
                if (KNOWN_ORGS.includes(country.organization.name))
                    state.onboardingCountry = country
                else state.onboardingCountry = undefined
            }
            else state.onboardingCountry = undefined
        },
        setPortalAccountDomain: (state: AppState, action: PayloadAction<PortalAccountDomain>) => {
            state.portalAccountDomain = action.payload
        },
        setOnboardingDomain: (state: AppState, action: PayloadAction<PortalAccountDomain | null>) => {
            state.onboardingDomain = action.payload
        },
        setPreferredLanguages: (state: AppState, action: PayloadAction<PreferredLanguage[]>) => {
            // temp workaround for zh lang
            const langs = action.payload.map(x => {
                if (x.language_code === 'zh') {
                    return {
                        ...x,
                        language_code: 'zh-Hans'
                    }
                }
                return x
            })
            state.preferredLanguages = langs
        },
        setIB: (state: AppState, action: PayloadAction<IBInfo>) => {
            state.ibInfo = action.payload
        },
        setPasswordResetToken: (state: AppState, action: PayloadAction<string | null>) => {
            state.passwordResetToken = action.payload
        },
        setAppStatus: (state: AppState, action: PayloadAction<ApplicationStatus | null>) => {
            if (!state.appStatus || (state.appStatus !== action.payload)) {
                state.oldAppStatus = state.appStatus
                state.appStatus = action.payload
                if (action.payload)
                    if (['APPROVED', ...finishedStatuses].includes(action.payload))
                        state.hasCompletedApp = true
            }
        },
        setAccounts: (state: AppState, action: PayloadAction<AccountStat[]>) => {
            const process = (accounts: AccountStat[]) => {
                accounts.forEach(a => {
                    a.marginLevel = Utils.calculateMarginLevel(a)
                })

            }
            const demo = action.payload.filter(a => a.account.type.toUpperCase() === 'DEMO'
                && (a.platformAccountType as string) !== 'FundedTrader')
            process(demo)
            //THPRT-5552 shift expiration date
/*             demo.forEach(a => {
                let numberOfDaysRemaining = a.account.expires?.numberOfDaysRemaining || 0
                if (Math.abs(numberOfDaysRemaining) > 14) {
                    numberOfDaysRemaining += 76
                    if (numberOfDaysRemaining >= 0) {
                        a.account.expires = {
                            accountExpirationDate: moment().utc().format(),
                            numberOfDaysRemaining: 0,
                            isExpired: true
                        }
                    }
                    else {
                        a.account.expires = {
                            accountExpirationDate: moment().add(Math.abs(numberOfDaysRemaining), 'days').utc().format(),
                            numberOfDaysRemaining,
                            isExpired: false
                        }

                    }
                }
            })
 */            state.demoAccounts = demo

            const live = action.payload.filter(a => a.account.type.toUpperCase() === 'LIVE'
                && (a.platformAccountType as string) !== 'ThinkMaster')
            state.totalBalanceInUSD = live.reduce((total, a) => {
                return total + a.balanceInUSD
            }, 0)
            process(live)
            state.liveAccounts = live
            //compatibility with legacy portal accounts
            if (!state.userApps.length && live.length > 0) {
                state.appStatus = "APPROVED"
                state.liveAppCompleted = true
            }
            state.accountsLoaded = true
        },
        clearAccounts: (state: AppState) => {
            state.accountsLoaded = false
        },
        setAppropriatenessLevel: (state: AppState, action: PayloadAction<AppropriatenessLevel>) => {
            state.appropriatenessLevel = action.payload
        },
        setApplicationApproval: (state: AppState, action: PayloadAction<ApplicationApproval>) => {
            state.applicationApproval = action.payload
        },
        setQuestions: (state: AppState, action: PayloadAction<Question[]>) => {
            state.questions = action.payload
        },
        canCreateAccounts: (state: AppState, action: PayloadAction<boolean>) => {
            state.canCreateAccounts = action.payload
        },
        setBankAccounts: (state: AppState, action: PayloadAction<BankAccount[] | null>) => {
            state.bankAccounts = action.payload
        },
        setSyOrgCountries: (state: AppState, action: PayloadAction<string[]>) => {
            state.syOrgCountries = action.payload
        },
        setAutoLogout: (state: AppState, action: PayloadAction<boolean>) => {
            state.autoLogout = action.payload
        },
        cleanupAppState: (state: AppState): AppState => {
            return {
                ...initialState,
                debugMode: state.debugMode,
                screen: state.screen,
                onboardingCountry: state.onboardingCountry,
                userIP: state.userIP,
                portalAccountDomain: state.portalAccountDomain,
                countries: state.countries,
                organizations: state.organizations,
                phoneCountries: state.phoneCountries,
                passwordResetToken: state.passwordResetToken,
                docTypes: state.docTypes,
                preferredLanguages: state.preferredLanguages,
                language: state.language,
                rtl: state.rtl,
                initError: state.initError,
                autoLogout: state.autoLogout,
                userEmailPreverified: state.userEmailPreverified,
                newTradeAccountInfo: state.newTradeAccountInfo,
                isSubscriptionTerminated: state.isSubscriptionTerminated,
                landingPage: state.landingPage,
                allowedPlatforms: state.allowedPlatforms,
                inactivityTimeout: state.inactivityTimeout,
                originSrc: state.originSrc,
                // THPRT-6264: always clear activation state on logout regardless of
                // what initialState captured at module load time.
                demoActivationPending: false,
                demoActivationResults: [],
                demoActivationPortalPassword: undefined,
            }

        },
        showMigrationModal: (state: AppState, action: PayloadAction<boolean>) => {
            state.isMigrationModalShown = action.payload
        },
        setNewCreatedAccount: (state: AppState, action: PayloadAction<NewCreatedAccount | null>) => {
            state.newCreatedAccount = action.payload
        },
        setTradingTypes: (state: AppState, action: PayloadAction<AccountTradingTypeModel[] | null>) => {
            state.tradingTypes = action.payload
        },
        setAllowThinkInvest: (state: AppState, action: PayloadAction<boolean>) => {
            state.allowThinkInvest = action.payload
        },
        setTradingAccountTypeToCreate: (state: AppState, action: PayloadAction<AccountTradingTypeUnion>) => {
            state.tradingAccountToCreate = action.payload
        },
        setApplicationToCreate: (state: AppState, action: PayloadAction<ApplicationType | undefined>) => {
            state.applicationToCreate = action.payload
        },
        setUserInsidePortal: (state: AppState, action: PayloadAction<boolean>) => {
            state.userInsidePortal = action.payload
        },
        setLiveCongratsShown: (state: AppState, action: PayloadAction<boolean>) => {
            state.liveCongratsShown = action.payload
            Utils.setStorageBoolean(action.payload, __STORAGE.liveCongratsShown)
        },
        setShowFA2Warning: (state: AppState, action: PayloadAction<boolean>) => {
            state.showFA2Warning = action.payload
        },
        setOriginalExchangeRate: (state: AppState, action: PayloadAction<CurrencyPairRateResponse | null>) => {
            state.originalExchangeRate = action.payload
        },
        setSupportRequestTypes: (state: AppState, action: PayloadAction<SupportRequestType[]>) => {
            state.supportRequestTypes = action.payload
        },
        setInactivityTimeout: (state: AppState, action: PayloadAction<number>) => {
            state.inactivityTimeout = action.payload
        },
        setBlockedDepositAmountEnabled: (state: AppState, action: PayloadAction<boolean>) => {
            state.blockedDepositAmountEnabled = action.payload
        },
        setUserReferrerStatus: (state: AppState, action: PayloadAction<UserReferrerStatus>) => {
            state.userReferrerStatus = action.payload
        },
        setAllowedPlatforms: (state: AppState, action: PayloadAction<Array<TradingPlatformName> | undefined>) => {
            state.allowedPlatforms = action.payload
        },
        setLandingPage: (state: AppState, action: PayloadAction<{
            path: string
        } | undefined>) => {
            state.landingPage = action.payload
        },
        setLoyaltyStatus: (state: AppState, action: PayloadAction<LoyaltyStatus | null>) => {
            state.loyaltyStatus = action.payload
        },
        setApplicationCooldownCheck: (state: AppState, action: PayloadAction<boolean>) => {
            state.applicationCooldownCheck = action.payload
        },
        setDemoActivationPending: (state: AppState, action: PayloadAction<boolean>) => {
            state.demoActivationPending = action.payload
            if (action.payload) localStorage.setItem(__STORAGE.demoActivation, '1')
            else localStorage.removeItem(__STORAGE.demoActivation)
        },
        setDemoActivationResults: (state: AppState, action: PayloadAction<DemoActivationResult[]>) => {
            state.demoActivationResults = action.payload
        },
        // THPRT-6264: in-memory only; deliberately never written to storage.
        setDemoActivationPortalPassword: (state: AppState, action: PayloadAction<string | undefined>) => {
            state.demoActivationPortalPassword = action.payload
        },
    },
    extraReducers: {
        // ...ApiReducers.loginReducers,
        // ...ApiReducers.logoutReducers,
        ...ApiReducers.getBankAccounts,
        // ...ApiReducers.appStatusReducers
    }
})

const { actions, reducer } = slice

export const { changeLanguage,
    bootCompleted,
    debugMode,
    changeScreen,
    initError,
    setAppBusy,
    setUserCountry,
    setUserIP,
    userAppsLoaded,
    updateUserAppInfo,
    setUserAppInfo,
    organizationSettingsLoaded,
    setPreferredLanguages,
    setAppStatus,
    canCreateAccounts,
    docTypesLoaded,
    setUser,
    appError,
    cleanupAppState,
    setNewPortalAccount,
    setNewTradeAccountInfo,
    setIB,
    setPageTitle,
    setTempData,
    setPasswordResetToken,
    userEmailVerified,
    userEmailPreverified,
    jumioFailed,
    setAppropriatenessLevel,
    setApplicationApproval,
    setShowAppSidebar,
    notificationsLoaded,
    notificationMapLoaded,
    setTwoFa,
    dismissNotification,
    setNotificationsRead,
    setLastDeposit,
    setLiveAppCompleted,
    setCreatingLive,
    setKYCProvider,
    setMigration,
    setNextTermsAndConditions,
    setIsDemoExpired,
    setIsSubscriptionTerminated,
    userLoaded,
    showMigrationModal,
    setNewCreatedAccount,
    setTradingTypes,
    initCountries,
    setAccounts,
    clearAccounts,
    setAutoLogout,
    setOtpCodeSent,
    setQuestions,
    setLiveCongratsShown,
    setOriginalExchangeRate,
    setPortalAccountDomain,
    setTradingAccountTypeToCreate,
    setAllowThinkInvest,
    setRedirectedFromWTR,
    setOriginSrc,
    setAdditionalCFD,
    setLastWithdraw,
    setSubscriptionStatus,
    setSaveWtrFundsRedirection,
    setWtrFundsRoute,
    setOnboardingDomain,
    setShowFA2Warning,
    setSyOrgCountries,
    setSupportRequestTypes,
    setBlockedDepositAmountEnabled,
    setUserReferrerStatus,
    setInactivityTimeout,
    setLandingPage,
    setUserInsidePortal,
    setApplicationToCreate,
    setAllowedPlatforms,
    setLoyaltyStatus,
    setLoggedIn,
    setApplicationCooldownCheck,
    setDemoActivationPending,
    setDemoActivationResults,
    setDemoActivationPortalPassword,
} = actions

export default reducer
